// src/services/source-grounded-proposal-service.ts
// Client-side orchestrator for source-grounded proposal generation

import { supabase } from '../lib/supabase';
import { ProfileInventory } from '../lib/profile-inventory';
import { RequirementsMatcher } from '../lib/requirements-matcher';
import { GapAnalyzer } from '../lib/gap-analyzer';
import { extractStructuredRequirements } from '../lib/rfp-requirements-extractor';
import {
  SourceGroundedProposalOutput,
  ContractForMatching,
  RFPRequirement,
  ContractScoredLibrary,
  MatchingResult,
  GapReport,
  WinAssessment,
  ImprovementRoadmap,
} from '../types/source-grounded';

// ============================================================
// TYPES
// ============================================================

export interface SourceGroundedGenerationRequest {
  profileId: string;
  contractId: string;
  contract: {
    id: string;
    title: string;
    agency: string;
    naicsCode?: string;
    setAsides?: string[];
    fundingAmount?: number;
    description?: string;
    solicitationNumber?: string;
    dueDate?: string;
  };
  rfpAnalysis?: any; // Result from analyze-rfp
  solicitationContent?: string;
  sections?: string[]; // Optional: specific sections to generate
}

export interface GenerationProgress {
  stage: 'building_evidence' | 'extracting_requirements' | 'matching' | 'analyzing_gaps' | 'generating_deliverables' | 'generating' | 'complete' | 'error';
  progress: number; // 0-100
  message: string;
  details?: {
    evidenceCount?: number;
    requirementCount?: number;
    matchedCount?: number;
    gapCount?: number;
    deliverablesCount?: number;
    currentSection?: string;
  };
}

// Deliverables data types
export interface DeliverablesData {
  deliverables_with_labor: any[];
  labor_summary: {
    by_category: any[];
    total_hours: { min: number; max: number; recommended: number };
  };
  pricing_inputs: {
    labor_categories_required: string[];
    hours_matrix: Record<string, Record<string, number>>;
    suggested_rate_basis: string;
    sca_wage_determination_needed: boolean;
  };
  compliance_framework?: any;
  timeline_summary?: any;
  volume_summary?: any;
  metadata?: {
    generatedAt: string;
    source: string;
    deliverablesCount: number;
  };
}

export type ProgressCallback = (progress: GenerationProgress) => void;

// ============================================================
// MAIN SERVICE CLASS
// ============================================================

export class SourceGroundedProposalService {
  private profileInventory: ProfileInventory;
  private matcher: RequirementsMatcher;
  private gapAnalyzer: GapAnalyzer;

  constructor() {
    this.profileInventory = new ProfileInventory();
    this.matcher = new RequirementsMatcher();
    this.gapAnalyzer = new GapAnalyzer();
  }

  /**
   * Generate a source-grounded proposal
   */
  async generateProposal(
    request: SourceGroundedGenerationRequest,
    onProgress?: ProgressCallback
  ): Promise<SourceGroundedProposalOutput> {
    const { profileId, contractId, contract, rfpAnalysis, solicitationContent } = request;

    try {
      // Stage 1: Build evidence library
      this.reportProgress(onProgress, {
        stage: 'building_evidence',
        progress: 10,
        message: 'Building evidence library from profile...',
      });

      const scoredLibrary = await this.profileInventory.buildScoredLibrary(
        profileId,
        this.buildContractForMatching(contract)
      );

      this.reportProgress(onProgress, {
        stage: 'building_evidence',
        progress: 30,
        message: `Found ${scoredLibrary.totalEvidenceCount} evidence items`,
        details: { evidenceCount: scoredLibrary.totalEvidenceCount },
      });

      // Stage 2: Extract requirements
      this.reportProgress(onProgress, {
        stage: 'extracting_requirements',
        progress: 40,
        message: 'Extracting structured requirements...',
      });

      const requirements = this.extractRequirements(rfpAnalysis, contract);

      this.reportProgress(onProgress, {
        stage: 'extracting_requirements',
        progress: 50,
        message: `Extracted ${requirements.length} requirements`,
        details: { requirementCount: requirements.length },
      });

      // Stage 3: Match requirements to evidence
      this.reportProgress(onProgress, {
        stage: 'matching',
        progress: 55,
        message: 'Matching requirements to evidence...',
      });

      // Ensure requirements is an array
      const safeRequirements = Array.isArray(requirements) ? requirements : [];

      const matchingResult = this.matcher.matchRequirements(
        safeRequirements,
        scoredLibrary
      );

      this.reportProgress(onProgress, {
        stage: 'matching',
        progress: 65,
        message: `Matched ${matchingResult.fullMatches + matchingResult.partialMatches}/${matchingResult.totalRequirements} requirements`,
        details: { matchedCount: matchingResult.fullMatches + matchingResult.partialMatches },
      });

      // Stage 4: Analyze gaps
      this.reportProgress(onProgress, {
        stage: 'analyzing_gaps',
        progress: 70,
        message: 'Analyzing gaps and generating recommendations...',
      });

      const gapReport = this.gapAnalyzer.analyzeGaps(matchingResult, scoredLibrary);
      const winAssessment = this.gapAnalyzer.generateWinAssessment(matchingResult, scoredLibrary, gapReport);
      const roadmap = this.gapAnalyzer.generateImprovementRoadmap(matchingResult, gapReport);

      const criticalGapsCount = (gapReport?.criticalGaps || []).length;
      this.reportProgress(onProgress, {
        stage: 'analyzing_gaps',
        progress: 70,
        message: `Readiness: ${gapReport?.overallReadiness || 0}%, ${criticalGapsCount} critical gaps`,
        details: { gapCount: criticalGapsCount },
      });

      // Stage 5: Generate deliverables analysis (BEFORE proposal generation)
      console.log('🚨🚨🚨 DELIVERABLES STEP STARTING 🚨🚨🚨');
      this.reportProgress(onProgress, {
        stage: 'generating_deliverables',
        progress: 75,
        message: 'Analyzing deliverables and labor framework...',
      });

      let deliverablesData: DeliverablesData | null = null;
      try {
        deliverablesData = await this.generateDeliverablesAnalysis(request, rfpAnalysis);
        if (deliverablesData) {
          this.reportProgress(onProgress, {
            stage: 'generating_deliverables',
            progress: 80,
            message: `Generated ${deliverablesData.deliverables_with_labor?.length || 0} deliverables with labor framework`,
            details: { deliverablesCount: deliverablesData.deliverables_with_labor?.length || 0 },
          });
        }
      } catch (deliverableError) {
        console.warn('⚠️ Deliverables generation warning (continuing without):', deliverableError);
      }

      // Stage 6: Generate proposal via edge function
      this.reportProgress(onProgress, {
        stage: 'generating',
        progress: 85,
        message: 'Generating source-grounded proposal sections...',
      });

      const generationResult = await this.callGenerationEndpoint(
        request,
        scoredLibrary,
        matchingResult,
        gapReport,
        deliverablesData
      );

      this.reportProgress(onProgress, {
        stage: 'complete',
        progress: 100,
        message: 'Proposal generation complete',
      });

      // Combine results into final output
      return this.buildFinalOutput(
        request,
        scoredLibrary,
        matchingResult,
        gapReport,
        winAssessment,
        roadmap,
        generationResult,
        deliverablesData
      );

    } catch (error) {
      this.reportProgress(onProgress, {
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Get pre-generation analysis without generating content
   */
  async analyzeForProposal(
    request: SourceGroundedGenerationRequest
  ): Promise<{
    scoredLibrary: ContractScoredLibrary;
    matchingResult: MatchingResult;
    gapReport: GapReport;
    winAssessment: WinAssessment;
  }> {
    const { profileId, contract, rfpAnalysis } = request;

    console.log('📊 [Analysis] Starting proposal readiness analysis...');
    console.log('📊 [Analysis] Profile ID:', profileId);
    console.log('📊 [Analysis] Contract:', contract.title);

    // Build evidence library
    console.log('📊 [Analysis] Step 1/4: Building evidence library...');
    const startLibrary = Date.now();

    let scoredLibrary: ContractScoredLibrary;
    try {
      scoredLibrary = await this.profileInventory.buildScoredLibrary(
        profileId,
        this.buildContractForMatching(contract)
      );
      console.log(`📊 [Analysis] Step 1/4 complete in ${Date.now() - startLibrary}ms - Found ${scoredLibrary.totalEvidenceCount} evidence items`);
    } catch (error) {
      console.error('📊 [Analysis] Step 1/4 FAILED:', error);
      throw error;
    }

    // Extract requirements
    console.log('📊 [Analysis] Step 2/4: Extracting requirements...');
    const startReqs = Date.now();
    const requirements = this.extractRequirements(rfpAnalysis, contract);
    console.log(`📊 [Analysis] Step 2/4 complete in ${Date.now() - startReqs}ms - Extracted ${requirements.length} requirements`);

    // Match requirements
    console.log('📊 [Analysis] Step 3/4: Matching requirements to evidence...');
    const startMatch = Date.now();

    // Ensure requirements is an array
    const safeRequirements = Array.isArray(requirements) ? requirements : [];
    console.log(`📊 [Analysis] Matching ${safeRequirements.length} requirements`);

    const matchingResult = this.matcher.matchRequirements(
      safeRequirements,
      scoredLibrary
    );
    console.log(`📊 [Analysis] Step 3/4 complete in ${Date.now() - startMatch}ms - ${matchingResult.fullMatches} full, ${matchingResult.partialMatches} partial matches`);

    // Analyze gaps
    console.log('📊 [Analysis] Step 4/4: Analyzing gaps...');
    const startGaps = Date.now();
    const gapReport = this.gapAnalyzer.analyzeGaps(matchingResult, scoredLibrary);
    const winAssessment = this.gapAnalyzer.generateWinAssessment(matchingResult, scoredLibrary, gapReport);
    console.log(`📊 [Analysis] Step 4/4 complete in ${Date.now() - startGaps}ms - Readiness: ${gapReport.overallReadiness}%`);

    console.log('📊 [Analysis] ✅ Analysis complete!');

    return {
      scoredLibrary,
      matchingResult,
      gapReport,
      winAssessment,
    };
  }

  // ============================================================
  // PRIVATE HELPERS
  // ============================================================

  private reportProgress(callback: ProgressCallback | undefined, progress: GenerationProgress) {
    if (callback) {
      callback(progress);
    }
    console.log(`[${progress.stage}] ${progress.progress}%: ${progress.message}`);
  }

  private buildContractForMatching(contract: SourceGroundedGenerationRequest['contract']): Omit<ContractForMatching, 'requirements'> {
    return {
      id: contract.id,
      title: contract.title,
      agency: contract.agency,
      naicsCode: contract.naicsCode,
      setAsides: contract.setAsides,
      fundingAmount: contract.fundingAmount,
      description: contract.description,
    };
  }

  private extractRequirements(
    rfpAnalysis: any,
    contract: SourceGroundedGenerationRequest['contract']
  ): RFPRequirement[] {
    if (rfpAnalysis) {
      return extractStructuredRequirements(rfpAnalysis, {
        title: contract.title,
        agency: contract.agency,
        naicsCode: contract.naicsCode,
        setAsides: contract.setAsides,
        fundingAmount: contract.fundingAmount,
        description: contract.description,
      });
    }

    // Fallback: create basic requirements from contract description
    const requirements: RFPRequirement[] = [];

    if (contract.description) {
      requirements.push({
        id: 'REQ-GEN-001',
        text: `Deliver services as described: ${contract.description.substring(0, 200)}`,
        source: 'Contract Description',
        category: 'technical',
        mandatory: true,
        keywords: contract.description.toLowerCase().split(/\s+/).filter(w => w.length > 4).slice(0, 10),
      });
    }

    // Always include standard requirements
    requirements.push({
      id: 'REQ-PP-001',
      text: 'Demonstrated past performance on similar contracts',
      source: 'Standard Requirement',
      category: 'past_performance',
      mandatory: true,
      keywords: ['past performance', 'experience', 'similar work'],
    });

    if (contract.setAsides && contract.setAsides.length > 0) {
      requirements.push({
        id: 'REQ-COMP-001',
        text: `Meet set-aside requirements: ${contract.setAsides.join(', ')}`,
        source: 'Set-Aside Requirement',
        category: 'compliance',
        mandatory: true,
        keywords: contract.setAsides.map(s => s.toLowerCase()),
      });
    }

    return requirements;
  }

  /**
   * Generate deliverables analysis using the three-stage pipeline
   */
  private async generateDeliverablesAnalysis(
    request: SourceGroundedGenerationRequest,
    rfpAnalysis: any
  ): Promise<DeliverablesData | null> {
    console.log('📦 [Deliverables] Starting deliverables analysis...');

    try {
      // Prepare RFP content for extraction
      const rfpContent = rfpAnalysis?.fullText ||
        rfpAnalysis?.content ||
        request.solicitationContent ||
        request.contract.description ||
        '';

      if (!rfpContent || rfpContent.length < 100) {
        console.warn('⚠️ [Deliverables] Insufficient RFP content for deliverables extraction');
        return null;
      }

      // Stage 1: Extract raw deliverables using Gemini
      console.log('📦 [Deliverables] Stage 1/3: Extracting raw deliverables...');
      console.log('📦 [Deliverables] RFP content length:', rfpContent.length);
      const { data: rawDeliverables, error: extractError } = await supabase.functions.invoke(
        'extract-deliverables-raw',
        {
          body: {
            rfq_text: rfpContent,  // Edge function expects rfq_text
            rfq_attachments: [],
            contract_metadata: {   // Edge function expects contract_metadata
              notice_id: request.contract.id || '',
              title: request.contract.title,
              contract_type: 'service',
              naics_code: request.contract.naicsCode || '',
              agency: request.contract.agency,
              estimated_value: request.contract.fundingAmount,
            },
          },
        }
      );

      if (extractError) {
        console.error('❌ [Deliverables] Extract error:', extractError);
        throw extractError;
      }

      if (!rawDeliverables?.deliverables?.length) {
        console.warn('⚠️ [Deliverables] No deliverables extracted from RFP');
        return null;
      }

      console.log(`📦 [Deliverables] Extracted ${rawDeliverables.deliverables.length} raw deliverables`);

      // Stage 2: Enrich with compliance requirements using GPT-5
      console.log('📦 [Deliverables] Stage 2/3: Enriching with compliance requirements...');
      const { data: enrichedData, error: enrichError } = await supabase.functions.invoke(
        'enrich-deliverables-compliance',
        {
          body: {
            extracted_deliverables: {  // Edge function expects extracted_deliverables
              deliverables: rawDeliverables.deliverables,
              timeline_summary: rawDeliverables.timeline_summary || {},
              volume_summary: rawDeliverables.volume_summary || {},
            },
            contract_metadata: {  // Edge function expects contract_metadata
              notice_id: request.contract.id || '',
              title: request.contract.title,
              contract_type: 'service',
              naics_code: request.contract.naicsCode || '',
              agency: request.contract.agency,
              estimated_value: request.contract.fundingAmount,
            },
            far_clauses: [],
            agency_requirements: null,
          },
        }
      );

      if (enrichError) {
        console.error('❌ [Deliverables] Enrich error:', enrichError);
        // Continue with raw deliverables if enrichment fails
        console.warn('⚠️ [Deliverables] Continuing with raw deliverables');
      }

      const deliverablesForLabor = enrichedData?.deliverables || rawDeliverables.deliverables;

      // Stage 3: Generate labor framework using GPT-5
      console.log('📦 [Deliverables] Stage 3/3: Generating labor framework...');
      const { data: laborData, error: laborError } = await supabase.functions.invoke(
        'generate-labor-framework',
        {
          body: {
            enriched_deliverables: {  // Edge function expects enriched_deliverables
              deliverables: deliverablesForLabor,
              compliance_framework: enrichedData?.compliance_framework || {},
            },
            user_profile: {
              labor_categories: [],
              past_performance: [],
            },
            contract_constraints: {  // Edge function expects contract_constraints
              hours_ceiling: request.contract.fundingAmount ? Math.round(request.contract.fundingAmount / 100) : null,
              budget_ceiling: request.contract.fundingAmount || null,
              period_months: 12,
            },
          },
        }
      );

      if (laborError) {
        console.error('❌ [Deliverables] Labor framework error:', laborError);
        throw laborError;
      }

      // Stage 4: Synthesize product definition
      console.log('📦 [Deliverables] Stage 4/4: Synthesizing product definition...');
      let productDefinition = null;
      try {
        const { data: synthesized, error: synthesizeError } = await supabase.functions.invoke(
          'synthesize-product',
          {
            body: {
              deliverables: laborData?.deliverables_with_labor?.map((d: any) => ({
                id: d.deliverable_id,
                name: d.deliverable_name,
                description: d.description,
                type: d.type,
                category: d.category,
                quantity: d.quantity,
                unit: d.unit,
                specifications: d.specifications
              })) || rawDeliverables.deliverables,
              compliance_requirements: enrichedData?.compliance_requirements || rawDeliverables.compliance_requirements,
              labor_summary: laborData?.labor_summary,
              contract_metadata: {
                notice_id: request.contract.id || '',
                title: request.contract.title,
                naics_code: request.contract.naicsCode || '',
                agency: request.contract.agency,
                contract_type: 'service',
                estimated_value: request.contract.fundingAmount,
                period_of_performance: rawDeliverables.timeline_summary?.period_of_performance
              },
              document_type: rawDeliverables.document_type,
              rfq_summary: rfpContent?.substring(0, 2000)
            }
          }
        );

        if (!synthesizeError && synthesized && synthesized.product_name) {
          productDefinition = synthesized;
          console.log(`✅ [Deliverables] Product synthesized: "${synthesized.product_name}"`);
        } else if (synthesizeError) {
          console.warn('⚠️ [Deliverables] Product synthesis warning:', synthesizeError);
        }
      } catch (synthErr) {
        console.warn('⚠️ [Deliverables] Product synthesis error (continuing):', synthErr);
      }

      // Combine into final deliverables data
      const result: DeliverablesData = {
        deliverables_with_labor: laborData?.deliverables_with_labor || deliverablesForLabor,
        labor_summary: laborData?.labor_summary || {
          by_category: [],
          total_hours: { min: 0, max: 0, recommended: 0 },
        },
        pricing_inputs: laborData?.pricing_inputs || {
          labor_categories_required: [],
          hours_matrix: {},
          suggested_rate_basis: 'GSA Schedule',
          sca_wage_determination_needed: false,
        },
        compliance_framework: enrichedData?.compliance_framework,
        timeline_summary: laborData?.timeline_summary,
        volume_summary: laborData?.volume_summary,
        product_definition: productDefinition,
        metadata: {
          generatedAt: new Date().toISOString(),
          source: 'source-grounded-pipeline',
          deliverablesCount: laborData?.deliverables_with_labor?.length || deliverablesForLabor?.length || 0,
        },
      };

      console.log(`✅ [Deliverables] Complete: ${result.deliverables_with_labor?.length || 0} deliverables with labor framework`);
      return result;

    } catch (error) {
      console.error('❌ [Deliverables] Analysis failed:', error);
      throw error;
    }
  }

  private async callGenerationEndpoint(
    request: SourceGroundedGenerationRequest,
    scoredLibrary: ContractScoredLibrary,
    matchingResult: MatchingResult,
    gapReport: GapReport,
    deliverablesData: DeliverablesData | null
  ): Promise<any> {
    // Extract CLIN items from RFP analysis if available
    const clinItems = this.extractCLINItems(request.rfpAnalysis);

    // Prepare payload for edge function
    const payload = {
      contractId: request.contractId,
      contractTitle: request.contract.title,
      contractAgency: request.contract.agency,
      companyName: scoredLibrary.scoredEvidence[0]?.metadata?.companyName || 'Company',
      profileId: request.profileId,
      scoredEvidence: (scoredLibrary.scoredEvidence || []).map(e => ({
        evidenceId: e.evidenceId,
        sourceType: e.sourceType,
        contentSummary: e.contentSummary,
        fullContent: e.fullContent,
        keywords: e.keywords || [],
        relevance: {
          tier: e.relevance?.tier || 'MINIMAL',
          overallScore: e.relevance?.overallScore || 0,
        },
        inferredCapabilities: (e.inferredCapabilities || []).map(ic => ({
          capabilityName: ic.capabilityName,
          confidence: ic.confidence,
          inferenceChain: ic.inferenceChain,
        })),
      })),
      requirements: (matchingResult.matches || []).map(m => ({
        requirementId: m.requirementId,
        requirementText: m.requirementText,
        requirementSource: m.requirementSource,
        requirementCategory: m.requirementCategory,
        matchStatus: m.matchStatus,
        matchedEvidence: (m.matchedEvidence || []).map(e => ({
          evidenceId: e.evidenceId,
          content: e.content,
        })),
        gaps: m.gaps || [],
        recommendations: m.recommendations || [],
      })),
      gapReport: {
        overallReadiness: gapReport.overallReadiness,
        competitivePosition: gapReport.competitivePosition,
        criticalGaps: gapReport.criticalGaps,
        improvementOpportunities: gapReport.improvementOpportunities,
        strengthsToHighlight: gapReport.strengthsToHighlight,
      },
      sections: request.sections,
      // Include deliverables data for pricing section generation
      deliverablesData: deliverablesData ? {
        deliverables_with_labor: deliverablesData.deliverables_with_labor,
        labor_summary: deliverablesData.labor_summary,
        pricing_inputs: deliverablesData.pricing_inputs,
        compliance_framework: deliverablesData.compliance_framework,
      } : null,
      // NEW: CLIN structure from solicitation
      clinItems: clinItems,
      // NEW: Contract metadata for pricing
      contractMetadata: {
        contractType: request.rfpAnalysis?.pricingStructure?.type || null,
        estimatedValue: request.contract.fundingAmount || null,
        periodOfPerformance: request.rfpAnalysis?.periodOfPerformance || null,
        setAsides: request.contract.setAsides || [],
      },
    };

    const { data, error } = await supabase.functions.invoke('source-grounded-proposal', {
      body: payload,
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(`Proposal generation failed: ${error.message}`);
    }

    return data;
  }

  /**
   * Extract CLIN items from RFP analysis
   */
  private extractCLINItems(rfpAnalysis: any): Array<{ clinNumber: string; description: string; type?: string; quantity?: string; unit?: string }> {
    if (!rfpAnalysis) return [];

    const clinItems: Array<{ clinNumber: string; description: string; type?: string; quantity?: string; unit?: string }> = [];

    // Try multiple paths where CLINs might be stored
    const possibleClinSources = [
      rfpAnalysis?.pricingStructure?.clin_items,
      rfpAnalysis?.clins,
      rfpAnalysis?.solicitationRequirements?.pricingStructure?.clin_items,
      rfpAnalysis?.contractLineItems,
    ];

    for (const source of possibleClinSources) {
      if (Array.isArray(source) && source.length > 0) {
        for (const item of source) {
          if (typeof item === 'string') {
            // Parse string format like "CLIN 0001 - Services"
            const match = item.match(/CLIN\s*(\d+)\s*[-:]\s*(.+)/i);
            if (match) {
              clinItems.push({
                clinNumber: match[1].padStart(4, '0'),
                description: match[2].trim(),
              });
            } else {
              // Just use as description
              clinItems.push({
                clinNumber: String(clinItems.length + 1).padStart(4, '0'),
                description: item,
              });
            }
          } else if (typeof item === 'object' && item !== null) {
            clinItems.push({
              clinNumber: item.number || item.clinNumber || String(clinItems.length + 1).padStart(4, '0'),
              description: item.description || item.title || 'Services',
              type: item.type || item.contractType,
              quantity: item.quantity,
              unit: item.unit,
            });
          }
        }
        break; // Use first valid source
      }
    }

    return clinItems;
  }

  private buildFinalOutput(
    request: SourceGroundedGenerationRequest,
    scoredLibrary: ContractScoredLibrary,
    matchingResult: MatchingResult,
    gapReport: GapReport,
    winAssessment: WinAssessment,
    roadmap: ImprovementRoadmap,
    generationResult: any,
    deliverablesData: DeliverablesData | null
  ): SourceGroundedProposalOutput & { deliverablesData?: DeliverablesData; extractedRequirements?: any[] } {
    // Extract company name from evidence
    const companyName = (scoredLibrary?.scoredEvidence || []).find(e =>
      e.metadata?.companyName
    )?.metadata?.companyName as string || 'Company';

    // Build extracted requirements from matching result for Contract Analysis tab
    const extractedRequirements = (matchingResult.matches || []).map(m => ({
      id: m.requirementId,
      text: m.requirementText,
      source: m.requirementSource,
      category: m.requirementCategory,
      matchStatus: m.matchStatus,
      hasEvidence: m.matchedEvidence && m.matchedEvidence.length > 0,
      evidenceCount: m.matchedEvidence?.length || 0,
      gaps: m.gaps || [],
      recommendations: m.recommendations || [],
    }));

    return {
      generatedAt: new Date().toISOString(),
      contractId: request.contractId,
      contractTitle: request.contract.title,
      profileId: request.profileId,
      companyName,

      proposal: {
        coverLetter: generationResult.proposal?.coverLetter || this.createPlaceholderSection('cover_letter'),
        executiveSummary: generationResult.proposal?.executiveSummary || this.createPlaceholderSection('executive_summary'),
        technicalApproach: generationResult.proposal?.technicalApproach || this.createPlaceholderSection('technical'),
        managementApproach: generationResult.proposal?.managementApproach || this.createPlaceholderSection('management'),
        pastPerformance: generationResult.proposal?.pastPerformance || this.createPlaceholderSection('past_performance'),
        personnel: generationResult.proposal?.personnel || this.createPlaceholderSection('personnel'),
        pricingStrategy: generationResult.proposal?.pricingStrategy || this.createPlaceholderSection('pricing'),
        compliance: generationResult.proposal?.compliance || this.createPlaceholderSection('compliance'),
      },

      sourceMap: generationResult.sourceMap || {
        totalClaims: 0,
        claimsWithEvidence: 0,
        claimsWithInference: 0,
        traceabilityScore: 0,
        allCitations: [],
      },

      gapReport,
      winAssessment,
      improvementRoadmap: roadmap,

      evidenceLibrary: {
        totalItems: scoredLibrary.totalEvidenceCount,
        usedItems: generationResult.evidenceLibrary?.usedItems || 0,
        highRelevanceItems: scoredLibrary.summary.highRelevanceCount,
        inferredCapabilitiesCount: scoredLibrary.summary.totalInferredCapabilities,
      },

      // Include deliverables data if available
      ...(deliverablesData && { deliverablesData }),

      // Include extracted requirements for Contract Analysis tab
      extractedRequirements,
    };
  }

  private createPlaceholderSection(sectionType: string): any {
    const titles: Record<string, string> = {
      cover_letter: 'Cover Letter',
      executive_summary: 'Executive Summary',
      technical: 'Technical Approach',
      management: 'Management Approach',
      past_performance: 'Past Performance',
      personnel: 'Key Personnel',
      pricing: 'Pricing Strategy',
      compliance: 'Compliance Matrix',
    };

    return {
      sectionTitle: titles[sectionType] || sectionType,
      sectionType,
      content: `[${titles[sectionType]} - Generation pending]`,
      contentWithCitations: '',
      sourceCitations: [],
      requirementsCovered: [],
      gapsInSection: [],
      confidenceScore: 0,
      wordCount: 0,
    };
  }
}

// ============================================================
// SINGLETON INSTANCE
// ============================================================

let serviceInstance: SourceGroundedProposalService | null = null;

export function getSourceGroundedProposalService(): SourceGroundedProposalService {
  if (!serviceInstance) {
    serviceInstance = new SourceGroundedProposalService();
  }
  return serviceInstance;
}

// ============================================================
// CONVENIENCE FUNCTIONS
// ============================================================

/**
 * Generate a source-grounded proposal
 */
export async function generateSourceGroundedProposal(
  request: SourceGroundedGenerationRequest,
  onProgress?: ProgressCallback
): Promise<SourceGroundedProposalOutput> {
  const service = getSourceGroundedProposalService();
  return service.generateProposal(request, onProgress);
}

/**
 * Analyze a contract for proposal readiness
 */
export async function analyzeProposalReadiness(
  request: SourceGroundedGenerationRequest
): Promise<{
  scoredLibrary: ContractScoredLibrary;
  matchingResult: MatchingResult;
  gapReport: GapReport;
  winAssessment: WinAssessment;
}> {
  const service = getSourceGroundedProposalService();
  return service.analyzeForProposal(request);
}
